// A System Test in the Matrix (issue 1532; docs/MRCS/shadowrun-3.md §9 step 18; the book p. 205, 209-212): where the
// table's system declares one (`features.combat.matrix`), a decker's operation on a host is a success contest. Every
// step is the system's data:
//
// 1. The decker's test: the skill's dice and any pool dice drawn for `matrix`, against the rating of the subsystem the
//    operation works on, raised by the host's alert, with the TN modifiers asked (the utilities that help it) and the
//    decker's wounds (which the server joins).
// 2. The host's test, thrown at the same time: its security dice against the decker's Detection Factor (read from the
//    decker's sheet, or typed).
// 3. The outcome: the decker succeeds with at least the data's `least` successes and more than the host's (or as many,
//    when ties go to the decker). Every success the host threw is added to the decker's security tally on that host.
//
// The host is the DM's, typed on the decker's token (rules/matrixHost.ts) and read there by the server; the record is a
// success test's (`TestFacts.matrix`, rules/test.ts), its first side the decker's. A player is sent `playerMatrixFacts`
// of it: their own dice and successes and whether the operation succeeded — never the host's numbers, its TN or the
// tally, unless the DM shows the host's ratings, and then still never the tally.
//
// Nothing here throws a die on its own: the server hands the generator in, a test a scripted list.
import { MAX_POOL, poolDisaster, poolText, testDice } from '../dice/pool.js';
import { parseTestDice } from './system.js';
import { testDiceData } from './test.js';
import { parseThrow, successesOf, throwLine, throwOne } from './staged.js';
import { HOST_LIMITS } from './matrixHost.js';
export const MATRIX_TEST_LIMITS = { name: 80, rating: MAX_POOL, modifiers: 6, modifier: 50, label: 40, detection: 30 };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const ADDRESS_RE = /^[a-z][a-z0-9_]{0,62}\/[a-z0-9][a-z0-9-]{0,127}$/;
// eslint-disable-next-line no-control-regex
const LINE_BAD = /[\u0000-\u001f\u007f]/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
const isText = (v, max) => typeof v === 'string' && v.trim().length >= 1 && v.length <= max && !LINE_BAD.test(v);
const only = (v, keys) => Object.keys(v).every((k) => keys.includes(k));
const keyLabel = (v) => (isObj(v) && only(v, ['key', 'label']) && typeof v.key === 'string' && KEY_RE.test(v.key) && isText(v.label, 40) ? { key: v.key, label: v.label.trim() } : null);
function parseTerms(v) {
    if (v === undefined)
        return [];
    if (!Array.isArray(v) || v.length > MATRIX_TEST_LIMITS.modifiers)
        return null;
    const out = [];
    for (const x of v) {
        if (!isObj(x) || !only(x, ['label', 'value']) || !isText(x.label, MATRIX_TEST_LIMITS.label) || !isInt(x.value, -MATRIX_TEST_LIMITS.modifier, MATRIX_TEST_LIMITS.modifier))
            return null;
        out.push({ label: x.label.trim(), value: x.value });
    }
    return out;
}
/** A System Test request from the wire: exact shape or null. Untrusted input, always. */
export function parseMatrixTestRequest(v) {
    if (!isObj(v) || v.kind !== 'matrix.test')
        return null;
    if (!only(v, ['kind', 'decker', 'subsystem', 'skill', 'pool', 'modifiers', 'detection', 'private']))
        return null;
    if (!UUID_RE.test(String(v.decker)) || typeof v.subsystem !== 'string' || !KEY_RE.test(v.subsystem))
        return null;
    const s = v.skill;
    if (!isObj(s) || !only(s, ['name', 'rating', 'ref']) || !isText(s.name, MATRIX_TEST_LIMITS.name) || !isInt(s.rating, 1, MATRIX_TEST_LIMITS.rating) || (s.ref !== undefined && (typeof s.ref !== 'string' || !ADDRESS_RE.test(s.ref))))
        return null;
    let pool;
    if (v.pool !== undefined) {
        const p = v.pool;
        if (!isObj(p) || !only(p, ['key', 'dice']) || typeof p.key !== 'string' || !KEY_RE.test(p.key) || !isInt(p.dice, 1, MATRIX_TEST_LIMITS.rating) || s.rating + p.dice > MAX_POOL)
            return null;
        pool = { key: p.key, dice: p.dice };
    }
    const modifiers = parseTerms(v.modifiers);
    if (!modifiers || (v.detection !== undefined && !isInt(v.detection, 1, MATRIX_TEST_LIMITS.detection)) || (v.private !== undefined && typeof v.private !== 'boolean'))
        return null;
    return {
        kind: 'matrix.test', decker: String(v.decker), subsystem: v.subsystem, skill: { name: s.name.trim(), rating: s.rating, ...(typeof s.ref === 'string' ? { ref: s.ref } : {}) },
        ...(pool ? { pool } : {}), modifiers, ...(typeof v.detection === 'number' ? { detection: v.detection } : {}), private: v.private === true,
    };
}
export const NO_MATRIX_RULES = "This table's game system names no System Test, so the table cannot make one.";
/** What a decker is told when the DM has put no host on their token. */
export const NO_HOST = 'The DM has set no host for this token yet.';
const WOUNDED = 'wounded';
/** A System Test made concrete: both tests thrown with the generator by the system's rules. A string is a refusal in words. */
export function makeMatrixTest(req, m, rng) {
    const r = m.rules;
    const sub = r.subsystems.find((x) => x.key === req.subsystem);
    if (!sub)
        return 'That is not one of this game system\'s subsystems.';
    const rating = m.host.ratings[sub.key];
    if (rating === undefined)
        return `The host has no ${sub.label} rating: the DM sets it.`;
    const code = r.codes.find((c) => c.key === m.host.code);
    if (!code)
        return 'The host\'s security code is not one of this game system\'s: the DM sets it again.';
    if (m.detection === null)
        return `The decker's ${r.detection.label} could not be read from its sheet: type it.`;
    const alert = m.host.alert === null ? null : r.alerts.find((a) => a.key === m.host.alert) ?? null;
    const clamp = (n) => Math.max(-MATRIX_TEST_LIMITS.modifier, Math.min(MATRIX_TEST_LIMITS.modifier, n));
    const terms = [
        ...(alert && alert.add ? [{ label: alert.label.toLowerCase().slice(0, MATRIX_TEST_LIMITS.label), value: alert.add }] : []),
        ...req.modifiers,
        ...(m.wound ? [{ label: WOUNDED, value: clamp(m.wound) }] : []),
    ];
    const spent = m.spent ? { key: m.spent.key, label: m.spent.label, dice: m.spent.dice } : null;
    const dice = req.skill.rating + (spent?.dice ?? 0);
    const decker = throwOne(r.dice, { label: req.skill.name, rating: req.skill.rating, spent, dice, target: rating, base: sub.label, terms }, rng);
    if (typeof decker === 'string')
        return decker;
    const security = throwOne(r.dice, { label: r.security, rating: m.host.security, spent: null, dice: m.host.security, target: m.detection, base: r.detection.label, terms: [] }, rng);
    if (typeof security === 'string')
        return security;
    const probes = [];
    for (const p of (m.probes ?? []).slice(0, HOST_LIMITS.steps)) {
        const t = throwOne(r.dice, { label: p.name, rating: p.rating, spent: null, dice: p.rating, target: m.detection, base: r.detection.label, terms: [] }, rng);
        if (typeof t === 'string')
            return t;
        probes.push({ name: p.name, throw: t });
    }
    const side = {
        name: m.who.name, ...(m.who.token ? { token: m.who.token } : {}), rated: { kind: 'skill', name: req.skill.name, rating: req.skill.rating, ...(req.skill.ref ? { ref: req.skill.ref } : {}) },
        dice, ...(m.spent ? { spent: { ...m.spent } } : {}), target: rating, terms, tn: decker.tn, pool: decker.pool,
    };
    return {
        open: false, sides: [side], rules: { dice: testDiceData(r.dice), open: null, ties: null },
        matrix: {
            subsystem: { ...sub }, ties: r.ties, least: r.least, shown: m.host.shown,
            host: { name: m.host.name ?? null, code: { ...code }, security: m.host.security, rating, alert: alert ? { ...alert } : null },
            detection: { value: m.detection, label: r.detection.label }, security, tally: { label: r.tally, before: m.tally },
            ...(probes.length ? { probes } : {}),
        },
    };
}
// ── The outcome ───────────────────────────────────────────────────────────
/** The decker's test as a throw, read back from its side. */
function deckerThrow(f) {
    const s = f.sides[0];
    return {
        label: s.rated.name, rating: s.rated.rating, spent: s.spent ? { key: s.spent.key, label: s.spent.label, dice: s.spent.dice } : null,
        dice: s.dice, target: s.target ?? 1, base: f.matrix.subsystem.label, terms: s.terms, tn: s.tn ?? 1, pool: s.pool,
    };
}
/** The contest's outcome, from the dice: the decker's successes against the host's, ties and the least by the data. */
export function matrixOutcome(f) {
    const m = f.matrix;
    if (m.seen)
        return { decker: m.seen.successes, host: m.security ? successesOf(f.rules.dice, m.security) : null, disaster: m.seen.disaster, won: m.seen.won };
    const t = deckerThrow(f);
    const decker = successesOf(f.rules.dice, t);
    const dd = parseTestDice(f.rules.dice);
    const concrete = dd && t.dice ? testDice(dd, { dice: t.dice, target: t.tn }) : null;
    const disaster = concrete ? poolDisaster(concrete, t.pool) : false;
    const host = m.security ? successesOf(f.rules.dice, m.security) : 0;
    const won = !disaster && decker >= m.least && (m.ties === 'decker' ? decker >= host : decker > host);
    return { decker, host, disaster, won };
}
/** The successes the IC that rolled at the test threw (qa-runs#1173), all onto the tally. */
export function probeSuccesses(f) {
    return (f.matrix.probes ?? []).reduce((n, p) => n + successesOf(f.rules.dice, p.throw), 0);
}
/** The tally after the test: what it was, every success the host threw (p. 211) and its probes', at most the bound. */
export function tallyAfter(f) {
    const o = matrixOutcome(f);
    return Math.min(HOST_LIMITS.tally, (f.matrix.tally?.before ?? 0) + (o.host ?? 0) + probeSuccesses(f));
}
// ── A player's copy ───────────────────────────────────────────────────────
/**
 * What a player is sent of a System Test: their own dice and successes and whether it succeeded. Without the host
 * shown, the side carries no TN, no base and no modifiers (the subsystem's rating less the utilities would name it), and
 * nothing of the host; shown, the host's ratings and test too. Never the tally.
 */
export function playerMatrixFacts(f) {
    const o = matrixOutcome(f);
    const m = f.matrix;
    const seen = { successes: o.decker, disaster: o.disaster, won: o.won };
    const head = { subsystem: { ...m.subsystem }, ties: m.ties, least: m.least, shown: m.shown, seen };
    const s = f.sides[0];
    if (m.shown && m.host && m.detection && m.security) {
        return { ...f, sides: [{ ...s, terms: s.terms.map((t) => ({ ...t })), pool: s.pool.map((d) => ({ ...d, faces: [...d.faces] })) }], matrix: { ...head, host: { ...m.host, code: { ...m.host.code }, alert: m.host.alert ? { ...m.host.alert } : null }, detection: { ...m.detection }, security: { ...m.security, terms: [], pool: m.security.pool.map((d) => ({ ...d, faces: [...d.faces] })) } } };
    }
    const side = { name: s.name, ...(s.token ? { token: s.token } : {}), rated: { ...s.rated }, dice: s.dice, ...(s.spent ? { spent: { ...s.spent } } : {}), target: null, terms: [], tn: null, pool: s.pool.map((d) => ({ ...d, faces: [...d.faces] })) };
    return { open: false, sides: [side], rules: f.rules, matrix: head };
}
/** A System Test's roll as a player is sent it (the event and the log's page): its test their copy, no fudge, no band. */
export function playerMatrixRoll(r) {
    const { band: _band, ...rest } = r;
    return { ...rest, dice: [...r.dice], fudged: null, ...(r.test && isMatrixTest(r.test) ? { test: playerMatrixFacts(r.test) } : {}) };
}
// ── Stored records ────────────────────────────────────────────────────────
/** Stored System Test facts beside a test's: exact shape or null. The side's TN is the subsystem's rating, the host's the Detection Factor. */
export function parseMatrixFacts(v, rules, side) {
    if (!isObj(v) || !only(v, ['subsystem', 'ties', 'least', 'shown', 'host', 'detection', 'security', 'tally', 'probes']) || !side)
        return null;
    const d = parseTestDice(rules);
    const subsystem = keyLabel(v.subsystem);
    if (!d || !subsystem || !(v.ties === 'decker' || v.ties === 'host') || !isInt(v.least, 0, 5) || typeof v.shown !== 'boolean')
        return null;
    const h = v.host;
    if (!isObj(h) || !only(h, ['name', 'code', 'security', 'rating', 'alert']) || !(h.name === null || isText(h.name, HOST_LIMITS.name)))
        return null;
    const code = keyLabel(h.code);
    if (!code || !isInt(h.security, 1, HOST_LIMITS.security) || !isInt(h.rating, 1, HOST_LIMITS.rating))
        return null;
    let alert = null;
    if (h.alert !== null) {
        const a = h.alert;
        const kl = isObj(a) ? keyLabel({ key: a.key, label: a.label }) : null;
        if (!kl || !isObj(a) || !only(a, ['key', 'label', 'add']) || !isInt(a.add, 0, 10))
            return null;
        alert = { ...kl, add: a.add };
    }
    const det = v.detection;
    if (!isObj(det) || !only(det, ['value', 'label']) || !isInt(det.value, 1, MATRIX_TEST_LIMITS.detection) || !isText(det.label, 40))
        return null;
    const security = parseThrow(v.security, rules, d);
    if (!security || security.rating !== h.security || security.target !== det.value || security.spent || security.terms.length)
        return null;
    const tl = v.tally;
    if (!isObj(tl) || !only(tl, ['label', 'before']) || !isText(tl.label, 40) || !isInt(tl.before, 0, HOST_LIMITS.tally))
        return null;
    // The decker's side is thrown against the subsystem's rating.
    if (side.target !== h.rating || side.defaulted)
        return null;
    const probes = [];
    if (v.probes !== undefined) {
        if (!Array.isArray(v.probes) || !v.probes.length || v.probes.length > HOST_LIMITS.steps)
            return null;
        for (const p of v.probes) {
            if (!isObj(p) || !only(p, ['name', 'throw']) || !isText(p.name, 80))
                return null;
            const t = parseThrow(p.throw, rules, d);
            if (!t || t.target !== det.value || t.spent || t.terms.length)
                return null;
            probes.push({ name: p.name.trim(), throw: t });
        }
    }
    return {
        subsystem, ties: v.ties, least: v.least, shown: v.shown,
        host: { name: h.name, code, security: h.security, rating: h.rating, alert },
        detection: { value: det.value, label: det.label.trim() }, security, tally: { label: tl.label.trim(), before: tl.before },
        ...(probes.length ? { probes } : {}),
    };
}
// ── The lines ─────────────────────────────────────────────────────────────
const plural = (n) => `${n} success${n === 1 ? '' : 'es'}`;
/** The host's line: "Red-8 host, Access 11", with its alert ("…, passive alert") and name when it has them. */
function hostLine(m) {
    if (!m.host)
        return null;
    const h = m.host;
    return `${h.name ? `${h.name}: ` : ''}${h.code.label}-${h.security} host, ${m.subsystem.label} ${h.rating}${h.alert ? `, ${h.alert.label.toLowerCase()}` : ''}`;
}
/** The decker's line: "HeadCrash, Access Test: Computer 6 vs TN 7 (11 Access − 4 Deception): 2, 2, 3, 4, 5, 6→3 → 1 success". */
function deckerLine(f) {
    const s = f.sides[0];
    const head = `${s.name ? `${s.name}, ` : ''}${f.matrix.subsystem.label} Test`;
    if (f.matrix.host && s.tn !== null)
        return throwLine(head, f.rules.dice, deckerThrow(f));
    // A player's copy without the host: the dice and the successes, no TN.
    const dice = s.spent ? `${s.rated.name} ${s.rated.rating} + ${s.spent.dice} ${s.spent.label} = ${s.dice} dice` : `${s.rated.name} ${s.rated.rating}`;
    const o = matrixOutcome(f);
    return `${head}: ${dice}: ${poolText(s.pool)} → ${o.disaster ? 'disaster' : plural(o.decker)}`;
}
/** The outcome's line: "HeadCrash succeeds: 1 success against 0", "The host wins: 2 successes against 1; the Access Test fails". */
function outcomeLine(f) {
    const o = matrixOutcome(f);
    const what = `the ${f.matrix.subsystem.label} Test`;
    if (o.host === null)
        return o.won ? `The ${f.matrix.subsystem.label} Test succeeds.` : `The ${f.matrix.subsystem.label} Test fails.`;
    const who = f.sides[0].name ?? 'The decker';
    if (o.disaster)
        return `Disaster: ${what} fails`;
    if (o.won)
        return `${who} succeeds: ${plural(o.decker)} against ${o.host}`;
    if (o.decker < f.matrix.least)
        return `${o.decker ? plural(o.decker) : 'No successes'}: ${what} fails`;
    return `The host wins: ${plural(o.host)} against ${o.decker}; ${what} fails`;
}
/** Every line of a System Test: the host, both tests, the outcome and the tally — what the copy in hand holds of them. */
export function matrixLines(f) {
    const m = f.matrix;
    const lines = [];
    const h = hostLine(m);
    if (h)
        lines.push(h);
    lines.push(deckerLine(f));
    if (m.security)
        lines.push(throwLine('Security Test', f.rules.dice, m.security));
    for (const p of m.probes ?? [])
        lines.push(throwLine(p.name, f.rules.dice, p.throw));
    lines.push(outcomeLine(f));
    if (m.tally) {
        const o = matrixOutcome(f);
        const probed = m.probes?.length ? ` + ${probeSuccesses(f)} IC` : '';
        lines.push(`${m.tally.label} ${m.tally.before} + ${o.host ?? 0}${probed} = ${tallyAfter(f)}`);
    }
    return lines;
}
/** Whether a test's record is a System Test. */
export const isMatrixTest = (f) => f.matrix !== undefined;
//# sourceMappingURL=matrix.js.map